import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Button, Space, Tag, Typography, Input, message,
  Modal, Form, InputNumber, Switch, Popconfirm, Empty, DatePicker,
  Badge, Row, Col, Spin, Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined,
  CalendarOutlined, EnvironmentOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { marketingSiteAPI, scenarioSiteAdminAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function EventManagePage() {
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [regModal, setRegModal] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await marketingSiteAPI.list({ page: 1, page_size: 100 });
        const list = res.data?.sites || res.data || [];
        setSites(list);
        const first = list.find(s => s.scenario_id);
        if (first) setActiveSite(first);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!activeSite?.scenario_id) return;
    setEventsLoading(true);
    try {
      const res = await scenarioSiteAdminAPI.listEvents(activeSite.scenario_id);
      setEvents(res.data || []);
    } catch { /* ignore */ }
    finally { setEventsLoading(false); }
  }, [activeSite?.scenario_id]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const openCreate = () => {
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const openEdit = (evt) => {
    setEditingEvent(evt);
    form.setFieldsValue({
      title: evt.title,
      description: evt.description,
      location: evt.location,
      title_th: evt.title_th,
      description_th: evt.description_th,
      location_th: evt.location_th,
      max_attendees: evt.max_attendees,
      event_date: evt.event_date ? dayjs(evt.event_date) : null,
      registration_deadline: evt.registration_deadline ? dayjs(evt.registration_deadline) : null,
      is_active: evt.is_active,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!activeSite?.scenario_id) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        event_date: values.event_date?.toISOString() || null,
        registration_deadline: values.registration_deadline?.toISOString() || null,
      };
      if (editingEvent) {
        await scenarioSiteAdminAPI.updateEvent(activeSite.scenario_id, editingEvent.id, payload);
        message.success(t('events.updateSuccess'));
      } else {
        await scenarioSiteAdminAPI.createEvent(activeSite.scenario_id, payload);
        message.success(t('events.createSuccess'));
      }
      setModalOpen(false);
      form.resetFields();
      fetchEvents();
    } catch (err) {
      if (err.errorFields) message.error(t('common.fillRequired'));
      else message.error(`${t('common.operationFailed')}: ${err.message}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (eventId) => {
    if (!activeSite?.scenario_id) return;
    try {
      await scenarioSiteAdminAPI.deleteEvent(activeSite.scenario_id, eventId);
      message.success(t('events.deleteSuccess'));
      fetchEvents();
    } catch { message.error(t('common.deleteFailed')); }
  };

  const handleToggleActive = async (evt) => {
    if (!activeSite?.scenario_id) return;
    try {
      await scenarioSiteAdminAPI.updateEvent(activeSite.scenario_id, evt.id, { is_active: !evt.is_active });
      fetchEvents();
    } catch { message.error(t('common.updateFailed')); }
  };

  const openRegistrations = async (evt) => {
    setRegModal(evt);
    setRegLoading(true);
    try {
      const res = await scenarioSiteAdminAPI.listRegistrations(activeSite.scenario_id, evt.id);
      setRegistrations(res.data || []);
    } catch { setRegistrations([]); }
    finally { setRegLoading(false); }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  }

  if (!activeSite) {
    return (
      <Card>
        <Empty description={t('events.noSiteMessage')} />
      </Card>
    );
  }

  const columns = [
    {
      title: t('events.table.name'), dataIndex: 'title', width: 240,
      render: (v, evt) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          {evt.location && <Text type="secondary" style={{ fontSize: 12 }}><EnvironmentOutlined /> {evt.location}</Text>}
        </div>
      ),
    },
    {
      title: t('events.table.date'), dataIndex: 'event_date', width: 160,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: t('events.table.deadline'), dataIndex: 'registration_deadline', width: 160,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : t('common.unlimited'),
    },
    {
      title: t('events.table.quota'), width: 120, align: 'center',
      render: (_, evt) => (
        <span>
          <Badge count={evt.registration_count || 0} style={{ backgroundColor: '#52c41a' }} overflowCount={999} />
          {evt.max_attendees ? ` / ${evt.max_attendees}` : ` / ${t('common.unlimited')}`}
        </span>
      ),
    },
    {
      title: t('events.table.registrations'), width: 100, align: 'center',
      render: (_, evt) => (
        <Button type="link" onClick={() => openRegistrations(evt)}>
          <TeamOutlined /> {t('events.table.viewRegs')} ({evt.registration_count || 0})
        </Button>
      ),
    },
    {
      title: '語系', width: 80, align: 'center',
      render: (_, evt) => (
        <Space size={2}>
          <span>🇹🇼</span>
          {evt.title_th && <span>🇹🇭</span>}
        </Space>
      ),
    },
    {
      title: t('events.table.active'), width: 80, align: 'center',
      render: (_, evt) => (
        <Switch size="small" checked={evt.is_active} onChange={() => handleToggleActive(evt)} />
      ),
    },
    {
      title: t('events.table.actions'), width: 120,
      render: (_, evt) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(evt)} />
          <Popconfirm title={t('events.confirmDelete')} onConfirm={() => handleDelete(evt.id)} okText={t('common.delete')} cancelText={t('common.cancel')}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const regColumns = [
    { title: t('events.registrations.name'), dataIndex: 'contact_name', width: 100 },
    { title: t('events.registrations.company'), dataIndex: 'company_name', width: 140 },
    { title: t('events.registrations.email'), dataIndex: 'email', width: 200 },
    { title: t('events.registrations.phone'), dataIndex: 'phone', width: 120 },
    { title: t('events.registrations.jobTitle'), dataIndex: 'job_title', width: 100 },
    { title: t('events.registrations.note'), dataIndex: 'note', ellipsis: true },
    {
      title: t('events.registrations.createdAt'), dataIndex: 'created_at', width: 150,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Title level={4} style={{ margin: 0 }}>{t('events.pageTitle')}</Title>
          {sites.length > 1 && (
            <Tag color="blue">
              <GlobalOutlined /> {activeSite.title}
            </Tag>
          )}
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          {t('events.addButton')}
        </Button>
      </div>

      {sites.length > 1 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Text>{t('events.selectSite')}</Text>
            {sites.filter(s => s.scenario_id).map(s => (
              <Button
                key={s.id}
                type={activeSite?.id === s.id ? 'primary' : 'default'}
                size="small"
                onClick={() => setActiveSite(s)}
              >
                {s.title}
              </Button>
            ))}
          </Space>
        </Card>
      )}

      <Card bodyStyle={{ padding: 0 }}>
        {events.length === 0 && !eventsLoading ? (
          <Empty
            style={{ padding: 40 }}
            description={t('events.noEvents')}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('events.createFirst')}</Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={events}
            rowKey="id"
            loading={eventsLoading}
            pagination={events.length > 20 ? { pageSize: 20 } : false}
            size="middle"
          />
        )}
      </Card>

      {/* Event Create/Edit Modal */}
      <Modal
        title={editingEvent ? t('events.editTitle') : t('events.addTitle')}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={640}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Tabs defaultActiveKey="zh" items={[
            { key: 'zh', label: t('events.form.langZh'), children: (
              <>
                <Form.Item name="title" label={t('events.form.name')} rules={[{ required: true, message: t('events.form.nameRequired') }]}>
                  <Input placeholder={t('events.form.namePlaceholder')} />
                </Form.Item>
                <Form.Item name="description" label={t('events.form.description')}>
                  <TextArea rows={3} placeholder={t('events.form.descriptionPlaceholder')} />
                </Form.Item>
                <Form.Item name="location" label={t('events.form.location')}>
                  <Input placeholder={t('events.form.locationPlaceholder')} />
                </Form.Item>
              </>
            )},
            { key: 'th', label: t('events.form.langTh'), children: (
              <>
                <Form.Item name="title_th" label={t('events.form.nameTh')}>
                  <Input placeholder={t('events.form.nameThPlaceholder')} />
                </Form.Item>
                <Form.Item name="description_th" label={t('events.form.descriptionTh')}>
                  <TextArea rows={3} placeholder={t('events.form.descriptionThPlaceholder')} />
                </Form.Item>
                <Form.Item name="location_th" label={t('events.form.locationTh')}>
                  <Input placeholder={t('events.form.locationThPlaceholder')} />
                </Form.Item>
              </>
            )},
          ]} />
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="event_date" label={t('events.form.eventDate')}>
                <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={t('events.form.eventDatePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_attendees" label={t('events.form.maxAttendees')}>
                <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.unlimited')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="registration_deadline" label={t('events.form.deadline')}>
                <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={t('events.form.deadlinePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_active" label={t('events.form.activeLabel')} valuePropName="checked">
                <Switch checkedChildren={t('events.form.activeOn')} unCheckedChildren={t('events.form.activeOff')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Registrations Modal */}
      <Modal
        title={regModal ? t('events.registrations.title', { title: regModal.title, count: registrations.length }) : ''}
        open={!!regModal}
        onCancel={() => { setRegModal(null); setRegistrations([]); }}
        footer={null}
        width={900}
      >
        {regLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : registrations.length === 0 ? (
          <Empty description={t('events.registrations.empty')} />
        ) : (
          <Table
            columns={regColumns}
            dataSource={registrations}
            rowKey="id"
            pagination={registrations.length > 20 ? { pageSize: 20 } : false}
            size="small"
          />
        )}
      </Modal>
    </div>
  );
}
